import { BAND_LAYER_STEP, reachSortFraction, sortFraction, type SortReach } from "daedalus-core";

/**
 * How far above a ground layer's chunk its animated cells draw: under the next index, a whole depth above.
 */
const GROUND_CELL_NUDGE = 0.5;

/**
 * How far above its layer an animated cell in the Players band draws: one twentieth of `BAND_LAYER_STEP`, so
 * it stays under the next band layer and under every character its layer draws under.
 */
const BAND_CELL_NUDGE = BAND_LAYER_STEP / 20;

/**
 * How a row-sorted placement's rows sort: over the map's height, and over the rows beyond it when the chunk belongs to
 * a neighbouring map drawn beside it.
 */
export interface RowSort {
  readonly mapHeight: number;
  readonly reach?: SortReach;
}

/**
 * The fraction of a depth band a row of a row-sorted placement sorts at.
 *
 * @param rowSort The placement's row sort.
 * @param row The row in the map's tiles.
 * @returns The fraction: `sortFraction`, or `reachSortFraction` when the placement names a reach.
 */
export function rowSortFraction(rowSort: RowSort, row: number): number {
  return rowSort.reach === undefined ? sortFraction(row, rowSort.mapHeight) : reachSortFraction(row, rowSort.mapHeight, rowSort.reach);
}

/**
 * Where a chunk of a layer sits in the world (ADR-047): its layer, its chunk position and its base depth;
 * `band` says whether the layer is in the Players band, and `rowSort` that the layer draws per row there
 * (a tile layer in the band), with the map height the row fraction is taken over. A chunk of a neighbouring map drawn
 * beside the map (ADR-147 decision 6) gives its position in the map's chunk grid, negative north or west of it, and
 * `rowSort.reach`, the rows beyond the map that are drawn (`reachSortFraction`).
 */
export interface ChunkPlacement {
  readonly layerId: string;
  readonly cx: number;
  readonly cy: number;
  readonly depth: number;
  readonly band: boolean;
  readonly rowSort?: RowSort;
}

/**
 * The depth an animated cell of a chunk draws at (ADR-041 and its amendment of 2026-10-03): a hair above its
 * row's strip in a row-sorted placement; a hair above the chunk in a flat placement in the band, so flat
 * terrain such as water stays under every character, as its baked chunk does; half a depth above the chunk
 * on a ground layer, under the next index.
 *
 * @param placement The chunk's placement.
 * @param y The cell's top within the chunk, in pixels.
 * @param chunkSize Chunk size in tiles.
 * @param tileSize Tile size in pixels.
 * @returns The depth.
 */
export function animatedCellDepth(placement: ChunkPlacement, y: number, chunkSize: number, tileSize: number): number {
  if (placement.rowSort !== undefined) {
    return placement.depth + rowSortFraction(placement.rowSort, placement.cy * chunkSize + y / tileSize) + BAND_CELL_NUDGE;
  }
  return placement.depth + (placement.band ? BAND_CELL_NUDGE : GROUND_CELL_NUDGE);
}
